import { React, useState, useEffect } from 'react'
import { motion } from "framer-motion";
import { useParams } from "react-router-dom";
import { Helmet } from 'react-helmet'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendarDays, faCalendarPlus, faCirclePlay, faCircleCheck, faLock, faMagnifyingGlass, faArrowRight, faEnvelope, faPhone, faGlobe, faScaleBalanced } from '@fortawesome/free-solid-svg-icons'

import MainHeader from '../../../components/MainHeader/MainHeader.js'
import MainFooter from '../../../components/MainFooter/MainFooter.js'
import Skeleton from '../../../components/Skeleton/Skeleton.js'

import { lP, boxvar2_1, boxvar4, URL_MAINAPI, API_TOKEN, formatDateWithTime } from '../../../main.js'

import '../../../main.css'
import './EventList.css'

function eventHasLogo(ev) {
    return ev.logo_img != null && String(ev.logo_img).trim() !== ''
}

function applicationPhaseInfo(ev) {
    const d = ev.dates
    if (!d || d.applicationStart == null || d.applicationEnd == null) return null
    const now = Date.now()
    const appStart = new Date(d.applicationStart).getTime()
    const appEnd = new Date(d.applicationEnd).getTime()
    const enabled = Number(d.applicationEnabled) === 1
    if (!enabled) {
        return { label: 'Anmeldung geschlossen', icon: faLock, badgeClass: 'badge-closed', muted: true }
    }
    if (now < appStart) {
        return { label: 'Anmeldung zukünftig', icon: faCalendarPlus, badgeClass: 'badge-upcoming', muted: false }
    }
    if (now <= appEnd) {
        return { label: 'Anmeldung läuft', icon: faCirclePlay, badgeClass: 'badge-open', muted: false }
    }
    return { label: 'Anmeldung beendet', icon: faCircleCheck, badgeClass: 'badge-closed', muted: true }
}

export default function EventList() {

    const { orgid } = useParams()

    const [events, setEvents] = useState([])
    const [loaded, setLoaded] = useState(0)
    const [orgDetail, setOrgDetail] = useState(null)

    useEffect(() => {
        let cancelled = false

        async function load() {
            setLoaded(0)
            try {
                let url = URL_MAINAPI + "/event/public"
                if (orgid) url += "?orgid=" + encodeURIComponent(orgid)

                const res = await fetch(url, {
                    method: "GET",
                    headers: {
                        'Content-Type': 'application/json',
                        'Access-Control-Allow-Origin': '*',
                        'APITOKEN': API_TOKEN,
                    },
                })
                const data = await res.json()
                if (cancelled) return
                if (res.status === 200 && Array.isArray(data)) {
                    setEvents(data)
                } else {
                    setEvents([])
                }
            } catch (e) {
                if (!cancelled) setEvents([])
            }
            if (!cancelled) setLoaded(1)
        }

        load()

        if (orgid) {
            fetch(URL_MAINAPI + "/organization/" + orgid, {
                method: "GET",
                headers: {
                    'Content-Type': 'application/json',
                    'Access-Control-Allow-Origin': '*',
                    'APITOKEN': API_TOKEN,
                },
            }).then(r => r.json().then(data => ({ data, status: r.status })))
                .then(({ data, status }) => {
                    if (cancelled) return
                    if (status === 200 && data && data.name) setOrgDetail(data)
                    else setOrgDetail(null)
                })
                .catch(() => { if (!cancelled) setOrgDetail(null) })
        } else {
            setOrgDetail(null)
        }

        return () => { cancelled = true }
    }, [orgid])

    const orgName = orgDetail && orgDetail.name ? orgDetail.name : null
    const pageTitle = orgid
        ? (orgName ? `Organisation · ${orgName}` : 'Organisation')
        : 'Alle öffentlichen Events'

    const contact = orgDetail && orgDetail.contact ? orgDetail.contact : null
    const orgUrl = contact && contact.url && String(contact.url).trim() !== '' ? contact.url : null
    const orgInfoHasContent = orgDetail && (
        (orgDetail.description && String(orgDetail.description).trim() !== '') ||
        (contact && (contact.email || contact.tel || orgUrl || contact.imprint_url))
    )

    return (
        <div className='mainCon page' id='mainCon_eventlist'>
            <MainHeader selectedsite={""} />
            <Helmet>
                <meta charSet="utf-8" />
                <title>{pageTitle}</title>
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content="Übersicht öffentlicher Veranstaltungen auf Helferzeit" />
            </Helmet>

            <main className='page-body container section'>
                <div className='section-eyebrow'>Öffentliche Events</div>
                <motion.h1
                    variants={boxvar4}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, margin: "-50px" }}
                    className='section-title'
                >
                    {orgid
                        ? (orgName ? orgName : 'Organisation')
                        : 'Alle öffentlichen Events'}
                </motion.h1>
                <p className='section-sub'>
                    {orgid
                        ? 'Hier siehst du die öffentlich sichtbaren Veranstaltungen dieser Organisation. Klicke auf ein Event für Details und Anmeldung.'
                        : 'Finde Veranstaltungen, die Unterstützung suchen – der Anmeldestatus ist immer direkt sichtbar.'}
                </p>

                {orgid ? (
                    <p style={{ marginTop: '14px' }}>
                        <button type="button" className='btn btn-secondary btn-sm' onClick={() => lP('/events/public')}>
                            Alle Organisationen anzeigen
                        </button>
                    </p>
                ) : null}

                {/* Org-Banner */}
                {orgid && orgDetail && orgInfoHasContent ? (
                    <div className='org-banner' style={{ marginTop: '22px' }}>
                        {orgDetail.logo_img ? (
                            <img src={orgDetail.logo_img} alt={`Logo ${orgName || ''}`} />
                        ) : null}
                        <div>
                            {orgDetail.description ? (
                                <p className='org-banner-desc'>{orgDetail.description}</p>
                            ) : null}
                            <div className='org-banner-contact'>
                                {contact && contact.email ? (
                                    <a href={"mailto:" + contact.email}><FontAwesomeIcon icon={faEnvelope} /> {contact.email}</a>
                                ) : null}
                                {contact && contact.tel ? (
                                    <a href={"tel:" + contact.tel}><FontAwesomeIcon icon={faPhone} /> {contact.tel}</a>
                                ) : null}
                                {orgUrl ? (
                                    <a href={orgUrl} target='_blank' rel='noopener noreferrer'><FontAwesomeIcon icon={faGlobe} /> Webseite</a>
                                ) : null}
                                {contact && contact.imprint_url ? (
                                    <a href={contact.imprint_url} target='_blank' rel='noopener noreferrer'><FontAwesomeIcon icon={faScaleBalanced} /> Impressum</a>
                                ) : null}
                            </div>
                        </div>
                    </div>
                ) : null}

                {/* Loading / Empty */}
                {!loaded ? <Skeleton variant="grid" cols={2} count={4} /> : null}
                {loaded && events.length === 0 ? <p className='eventlist-status'>Keine öffentlichen Events gefunden.</p> : null}

                {/* Event-Cards */}
                <div className='event-cards' style={{ marginTop: '20px' }}>
                    {events.map(function (ev) {
                        const start = ev.dates && ev.dates.dateStart ? formatDateWithTime(new Date(ev.dates.dateStart)) : ''
                        const eventEnd = ev.dates && ev.dates.dateEnd ? formatDateWithTime(new Date(ev.dates.dateEnd)) : ''
                        const appStart = ev.dates && ev.dates.applicationStart ? formatDateWithTime(new Date(ev.dates.applicationStart)) : ''
                        const appEnd = ev.dates && ev.dates.applicationEnd ? formatDateWithTime(new Date(ev.dates.applicationEnd)) : ''
                        const phase = applicationPhaseInfo(ev)
                        const cardPastClass = phase && phase.muted ? ' is-muted' : ''
                        return (
                            <div
                                key={ev._id}
                                className={'card card-hover event-card noselect' + cardPastClass}
                                onClick={() => lP('/event/' + ev._id)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if (e.key === 'Enter') lP('/event/' + ev._id) }}
                            >
                                <div className='event-card-top'>
                                    {eventHasLogo(ev) ? (
                                        <img className='event-card-logo' src={ev.logo_img} alt="" />
                                    ) : (
                                        <div className='event-card-logo event-card-logo-placeholder' aria-hidden="true">
                                            <FontAwesomeIcon icon={faCalendarDays} />
                                        </div>
                                    )}
                                    <div>
                                        <h3>{ev.name || ev.longname}</h3>
                                        {ev.orgname && ev.orgid ? (
                                            <button
                                                type="button"
                                                className='event-card-org-link'
                                                onClick={(e) => { e.stopPropagation(); lP('/events/public/' + ev.orgid) }}
                                            >
                                                {ev.orgname}
                                            </button>
                                        ) : ev.orgname ? (
                                            <div className='event-card-org'>{ev.orgname}</div>
                                        ) : null}
                                    </div>
                                </div>

                                <div className='event-card-meta'>
                                    {appStart && appEnd ? (
                                        <span><FontAwesomeIcon icon={faCalendarPlus} /> Anmeldung: {appStart} – {appEnd}</span>
                                    ) : null}
                                    {start && eventEnd ? (
                                        <span><FontAwesomeIcon icon={faCalendarDays} /> {start} – {eventEnd}</span>
                                    ) : start ? (
                                        <span><FontAwesomeIcon icon={faCalendarDays} /> {start}</span>
                                    ) : null}
                                </div>

                                <div className='event-card-bottom'>
                                    {phase ? (
                                        <span className={'badge ' + phase.badgeClass}>
                                            <FontAwesomeIcon icon={phase.icon} /> {phase.label}
                                        </span>
                                    ) : null}
                                    <span className='btn btn-primary btn-sm'>
                                        Details <FontAwesomeIcon icon={faArrowRight} />
                                    </span>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </main>

            <MainFooter useEventStyle={true} />
        </div>
    )
}
